import { useCallbackRef } from '@talos/kyoko';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ManualRFQsMarketTabsWrapper } from '../../../../containers/Trading/Markets/ManualRFQs/ManualRFQsMarketTabsWrapper';
import { useAppStateDispatch, useAppStateSelector } from '../../../../providers/AppStateProvider';
import type { ManualRFQsPanelConfig } from '../../../../types/LayoutConfig';
import { Panel } from '../../../Layout/Components/Panel';
import { manualRFQPricingThunks } from '../../../OMS/ManualRFQPricing/ManualRFQPricingSlice';
import { selectCurrentLayout } from '../../AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '../../Layouts/tokens';
import type { LayoutPanelProps } from '../../types';

export const RESPOND_TO_QUOTE_QUERY_PARAM = 'send-quote'; // "send quote in response"

export function ManualRFQsPanel({ api, params }: LayoutPanelProps<ManualRFQsPanelConfig>) {
  // Deeplinking support. If the user passes a "?send-quote=RFQID" param, then we grab that and prime the ManualRFQPricingForm with it.
  const dispatch = useAppStateDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const hasPrimedRFQID = useRef(false); // we only ever want to do this once. useEffects are tricky.
  const layoutId = useAppStateSelector(selectCurrentLayout)?.id ?? DEFAULT_LAYOUT_ID;
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const rfqID = query.get(RESPOND_TO_QUOTE_QUERY_PARAM);
    if (rfqID && !hasPrimedRFQID.current) {
      dispatch(manualRFQPricingThunks.primeRFQID({ rfqID, navigate, layoutId }));
      hasPrimedRFQID.current = true;
    }
  }, [location.search, dispatch, navigate, layoutId]);

  const handleUpdatePanelData = useCallbackRef((panelData: Partial<ManualRFQsPanelConfig>) => {
    api.updateParameters(panelData);
  });

  return (
    <Panel panelApi={api} data-testid="manual-rfqs-panel">
      <ManualRFQsMarketTabsWrapper panelID={api.id} panelData={params} onUpdatePanelData={handleUpdatePanelData} />
    </Panel>
  );
}
